import { ChangeEvent, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiRequest } from "@/lib/queryClient";
import { formatPhoneInputValue } from "@/lib/phone";
import { insertClientSchema, type Client } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { AddressFields } from "@/components/forms/AddressFields";
import {
  composeAddressLine,
  defaultCountry,
  emptyAddressFields,
} from "@/lib/address";
import {
  Plus,
  Search,
  MessageSquare,
  Users,
  Calendar,
  HeartPulse,
  Sparkles,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  Upload,
  FileSpreadsheet,
} from "lucide-react";
import { StartMigrationDialog } from "@/components/migration/StartMigrationDialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { calendarService } from "@/lib/calendarService";
import { messagesService } from "@/lib/messagesService";
import { ENTITY_TYPES } from "@shared/entityTypes";

// clientSince is a timestamp in the DB but a plain string from <input type="date">
const clientFormSchema = insertClientSchema.extend({
  clientSince: z.string().optional().nullable(),
});
type ClientForm = z.infer<typeof clientFormSchema>;

type ClientRow = Client & { actualEmployeeCount?: number; logoUrl?: string | null };

type SortColumn = "name" | "contact" | "employees" | "status";
type SortDirection = "asc" | "desc";

export default function Clients() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("active");
  const [filterState, setFilterState] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [migrationTarget, setMigrationTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: clients = [], isLoading } = useQuery<ClientRow[]>({
    queryKey: ["/api/clients"],
  });

  // Fetch user's team assignments for producer auto-assignment
  interface UserTeam {
    teamId: number;
    teamName: string;
    teamLeadId: number | null;
    teamLeadName: string | null;
    isTeamLead: boolean;
  }
  const { data: userTeams = [] } = useQuery<UserTeam[]>({
    queryKey: ["/api/user/my-teams"],
  });
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);

  const form = useForm<ClientForm>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: "",
      primaryContactName: "",
      contactEmail: "",
      contactPhone: "",
      website: "",
      address: "",
      ...emptyAddressFields(),
      status: "active",
      ein: "",
      sic: "",
      entityType: "",
      clientSince: new Date().toISOString().split("T")[0],
    },
  });

  const createClientMutation = useMutation({
    mutationFn: async (data: ClientForm) => {
      const res = await apiRequest("POST", "/api/clients", data);
      return res.json();
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/contacts"] });
      setIsDialogOpen(false);
      form.reset();
      const employerEmail =
        result?.employerUser?.email || result?.client?.contactEmail;
      toast({
        title: "Client created",
        description: employerEmail
          ? `Welcome email sent to ${employerEmail}.`
          : "Client created successfully.",
        variant: "success",
      });
      if (result?.primaryEmployee?.id) {
        const pe = result.primaryEmployee as any;
        const fullName =
          [pe?.firstName, pe?.lastName]
            .filter(
              (v): v is string => typeof v === "string" && v.trim().length > 0,
            )
            .join(" ") || "Employee";
        toast({
          title: "Primary contact added as employee",
          description: `${fullName}${pe?.email ? ` — ${pe.email}` : ""}`,
          variant: "success",
        });
      }
      if (result?.client?.id) {
        navigate(`/clients/${result.client.id}`);
      }
    },
    onError: (err: any) => {
      toast({
        title: "Error",
        description:
          typeof err?.message === "string"
            ? err.message
            : "Failed to create client",
        variant: "destructive",
      });
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ClientForm }) => {
      const res = await apiRequest("PUT", `/api/clients/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      setIsDialogOpen(false);
      setEditingClient(null);
      form.reset();
      toast({
        title: "Success",
        description: "Client updated successfully",
        variant: "success",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Error",
        description:
          typeof err?.message === "string"
            ? err.message
            : "Failed to update client",
        variant: "destructive",
      });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/clients/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({
        title: "Success",
        description: "Client deleted successfully",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete client",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ClientForm) => {
    const addressLine = composeAddressLine(data);
    const payload: any = {
      ...data,
      address: addressLine || data.address || "",
      clientSince: data.clientSince ? new Date(data.clientSince + "T00:00:00") : null,
    };
    if (editingClient) {
      updateClientMutation.mutate({ id: editingClient.id, data: payload });
    } else {
      // Include selected team for producer auto-assignment
      if (selectedTeamId) {
        payload.teamId = selectedTeamId;
      }
      createClientMutation.mutate(payload);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this client?")) {
      deleteClientMutation.mutate(id);
    }
  };

  const handleScheduleMeeting = (client: Client) => {
    calendarService.scheduleMeeting({
      type: "client",
      id: client.id,
      name: client.name,
      email: client.contactEmail || undefined,
      phone: client.contactPhone || undefined,
    });
  };

  const stateOptions = Array.from(
    (clients || []).reduce<Map<string, string>>((acc, client) => {
      const rawState = (client as any)?.state;
      if (typeof rawState !== "string") return acc;
      const normalized = rawState.trim();
      if (!normalized) return acc;
      const key = normalized.toLowerCase();
      if (!acc.has(key)) {
        acc.set(key, normalized);
      }
      return acc;
    }, new Map()),
  )
    .map(([, state]) => state)
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 200);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortDirection === "asc" 
      ? <ChevronUp className="h-4 w-4 ml-1" />
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  const filteredClients = [...clients]
    .filter((client) => {
      const matchesSearch =
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (client.contactEmail || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" ||
        (client.status || "").toLowerCase() === filterStatus;
      const matchesState =
        filterState === "all" || ((client as any).state || "") === filterState;
      return matchesSearch && matchesStatus && matchesState;
    })
    .sort((a, b) => {
      const multiplier = sortDirection === "asc" ? 1 : -1;
      switch (sortColumn) {
        case "name":
          return multiplier * a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        case "contact":
          const emailA = (a.contactEmail || "").toLowerCase();
          const emailB = (b.contactEmail || "").toLowerCase();
          return multiplier * emailA.localeCompare(emailB);
        case "employees":
          const countA = (a as any).actualEmployeeCount ?? 0;
          const countB = (b as any).actualEmployeeCount ?? 0;
          return multiplier * (countA - countB);
        case "status":
          return multiplier * (a.status || "").localeCompare(b.status || "");
        default:
          return 0;
      }
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "inactive":
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    }
  };

  const formatPhone = (raw?: string | null) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
        7,
      )}`;
    }
    return String(raw).trim();
  };

  const handlePhoneFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    const formattedValue = formatPhoneInputValue(event.target.value);
    onChange(formattedValue);
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Clients</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage your client relationships and information
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/renewals">
              <Button variant="outline">
                <Calendar className="h-4 w-4 mr-2" />
                View Renewals
              </Button>
            </Link>
            <Link href="/migration/import">
              <PermissionButton
                permission="clients:create"
                whenDenied="hide"
                variant="outline"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Bulk import (CSV)
              </PermissionButton>
            </Link>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <PermissionButton
                permission="clients:create"
                whenDenied="hide"
                onClick={() => {
                  setEditingClient(null);
                  form.reset();
                  setSelectedTeamId(null);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </PermissionButton>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingClient ? "Edit Client" : "Add New Client"}
                </DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter company name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="primaryContactName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Contact Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter primary contact name"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="contactEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="contact@company.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="contactPhone"
                      render={({ field }) => {
                        const handleContactPhoneChange = (
                          event: ChangeEvent<HTMLInputElement>
                        ) => {
                          handlePhoneFieldChange(event, field.onChange);
                        };

                        return (
                          <FormItem>
                            <FormLabel>Contact Phone</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                value={field.value || ""}
                                placeholder="(555) 123-4567"
                                inputMode="tel"
                                maxLength={17}
                                onChange={handleContactPhoneChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Website</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="https://example.com"
                              {...field}
                              value={field.value || ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="ein"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>EIN</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="XX-XXXXXXX"
                              {...field}
                              value={field.value || ""}
                              maxLength={10}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                                if (digits.length > 2) {
                                  field.onChange(`${digits.slice(0, 2)}-${digits.slice(2)}`);
                                } else {
                                  field.onChange(digits);
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="sic"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SIC Code</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="XXXX"
                              {...field}
                              value={field.value || ""}
                              maxLength={4}
                              onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                                field.onChange(digits);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="entityType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Entity Type</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || ""}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {ENTITY_TYPES.map((et) => (
                                <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Address fields */}
                  <AddressFields control={form.control} />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="clientSince"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Client Since</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} value={field.value || ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Producer/Team selector - shown when user is on multiple teams */}
                  {!editingClient && userTeams.length > 1 && (
                    <div>
                      {/* A plain Label: this picker is React state, not a form field, and
                          FormLabel throws outside <FormField> — which crashed the whole
                          dialog for anyone on more than one team. */}
                      <Label>Producer / Team</Label>
                      <Select
                        value={selectedTeamId ? String(selectedTeamId) : ""}
                        onValueChange={(value) => setSelectedTeamId(value ? Number(value) : null)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select producer / team" />
                        </SelectTrigger>
                        <SelectContent>
                          {userTeams.map((team) => (
                            <SelectItem key={team.teamId} value={String(team.teamId)}>
                              {team.teamLeadName ? `${team.teamLeadName} — ${team.teamName}` : team.teamName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      disabled={createClientMutation.isPending || updateClientMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        createClientMutation.isPending ||
                        updateClientMutation.isPending
                      }
                    >
                      {(createClientMutation.isPending || updateClientMutation.isPending) && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      {editingClient 
                        ? (updateClientMutation.isPending ? "Updating..." : "Update")
                        : (createClientMutation.isPending ? "Creating..." : "Create")
                      }
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterState} onValueChange={setFilterState}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {stateOptions.map((st) => (
                      <SelectItem key={st} value={st}>
                        {st}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6">
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {filteredClients.length === 0 ? (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <p className="mb-4">No clients</p>
                    <PermissionButton
                      permission="clients:create"
                      whenDenied="hide"
                      onClick={() => {
                        setEditingClient(null);
                        form.reset();
                        setSelectedTeamId(null);
                        setIsDialogOpen(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Client
                    </PermissionButton>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleSort("name")}
                        >
                          <div className="flex items-center">
                            Client
                            {getSortIcon("name")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleSort("contact")}
                        >
                          <div className="flex items-center">
                            Contact
                            {getSortIcon("contact")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleSort("employees")}
                        >
                          <div className="flex items-center">
                            Employees (active)
                            {getSortIcon("employees")}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                          onClick={() => handleSort("status")}
                        >
                          <div className="flex items-center">
                            Status
                            {getSortIcon("status")}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredClients.map((client) => (
                        <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Avatar className="h-10 w-10 rounded-md bg-muted">
                                {client.logoUrl && (
                                  <AvatarImage src={client.logoUrl} alt={client.name} className="object-contain" />
                                )}
                                <AvatarFallback className="bg-primary text-white">
                                  {(client.name || "?")
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .slice(0, 2)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="ml-4">
                                <Link
                                  href={`/clients/${client.id}`}
                                  className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 cursor-pointer"
                                >
                                  {client.name || (
                                    <span className="italic text-gray-400">(Unnamed client)</span>
                                  )}
                                </Link>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                  {client.address ||
                                    [
                                      (client as any).address1,
                                      (client as any).address2,
                                      [
                                        (client as any).city,
                                        (client as any).state,
                                      ]
                                        .filter(Boolean)
                                        .join(", "),
                                      (client as any).zipCode,
                                    ]
                                      .filter(Boolean)
                                      .join(", ")}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {client.contactEmail ? (
                                <button
                                  type="button"
                                  className="text-blue-600 hover:underline"
                                  onClick={() =>
                                    messagesService.compose({
                                      to: client.contactEmail!,
                                      linkTo: { type: "client", id: client.id },
                                    })
                                  }
                                >
                                  {client.contactEmail}
                                </button>
                              ) : null}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {formatPhone(client.contactPhone)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            {(client as any).actualEmployeeCount ?? 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge className={getStatusColor(client.status)}>
                              {client.status}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/clients/${client.id}?tab=employees`)}
                                >
                                  <Users className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent variant="prominent">
                                Manage Employees
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleScheduleMeeting(client)}
                                >
                                  <Calendar className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent variant="prominent">
                                Schedule Meeting
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/clients/${client.id}?tab=contact-history`)}
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent variant="prominent">
                                Contact History
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/clients/${client.id}?tab=benefits`)}
                                >
                                  <Sparkles className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent variant="prominent">
                                Benefits
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <PermissionButton
                                  permission="clients:delete"
                                  whenDenied="hide"
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground hover:text-red-600"
                                  onClick={() => handleDelete(client.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </PermissionButton>
                              </TooltipTrigger>
                              <TooltipContent variant="prominent">
                                Delete Client
                              </TooltipContent>
                            </Tooltip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
